import { cn } from '../utils';

interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  size?: 'sm' | 'md';
  readOnly?: boolean;
  className?: string;
}

const SCALE = [1, 2, 3, 4, 5];

/**
 * Numbered 1–5 rating scale. Replaces the previous star metaphor (which read as a
 * consumer product review) with a segmented numeric control. Same props/data as
 * before, so call sites are unchanged. Interactive renders a segmented 1–5 picker;
 * read-only renders a compact "n / 5" badge.
 */
export function StarRating({
  value,
  onChange,
  size = 'md',
  readOnly = false,
  className,
}: StarRatingProps) {
  if (readOnly) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-md bg-secondary px-1.5 py-0.5 text-xs font-medium tabular-nums',
          className,
        )}
      >
        <span className={value ? 'text-primary' : 'text-muted-foreground'}>{value || '—'}</span>
        <span className="text-muted-foreground">/ 5</span>
      </span>
    );
  }

  const segClass = size === 'sm' ? 'h-6 min-w-6 px-1.5 text-xs' : 'h-8 min-w-8 px-2 text-sm';

  return (
    <div
      role="radiogroup"
      className={cn(
        'inline-flex items-center divide-x divide-border overflow-hidden rounded-md border border-input',
        className,
      )}
    >
      {SCALE.map((n) => {
        const selected = n === value;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`Rate ${n}`}
            onClick={() => onChange?.(n)}
            className={cn(
              'inline-flex items-center justify-center font-medium tabular-nums transition focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary',
              segClass,
              selected
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}
